import { toSafeObjectOrNull as toObject } from '@shared/utils';
import { shouldHideToolCallInActivityFeed, shouldHideToolResultInActivityFeed, shouldHideMessageInActivityFeed } from '@shared/utils/pieveActivityFeedVisibility.js';
import getPieveToolCallDisplayText from '@/lib/platform/getPieveToolCallDisplayText.js';
import { parseToolResultOutputFromEvent, toToolCallSegments } from '@/lib/threadEvents/toolCallActivityHelpers.js';
import toPieveEquivalentToolCall from '@shared/pieve/toPieveEquivalentToolCall.js';

// NOTE: 'reasoning' is deliberately NOT a raw pass-through type — persisted reasoning events
// carry their text in payload.text, so they must go through the dedicated eventType ===
// 'reasoning' branch below (which also handles the live-stream shape with top-level text).
const KNOWN_ACTIVITY_TYPES = new Set(['bubble', 'line', 'codeBlock', 'searchResults', 'fileChangeCard', 'approval', 'sources', 'divider', 'artifactPreview', 'notice', 'triggerPayloadCard']);
const CHECKLIST_UPDATE_TOOL_NAME = 'checklist_update';

const toSafeSortString = (value) => {
  return String(value || '').trim();
};

const toSegments = (text, tone = 'muted') => {
  const normalized = String(text || '').trim();

  if (!normalized) {
    return [];
  }

  return [{ text: normalized, tone }];
};

const truncateText = ({ value, maxLength = 1400 }) => {
  const text = String(value || '');

  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength)}…`;
};

const asPlainObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return value;
};

const isSpillStubPayload = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  return value?.payloadSpilled === true;
};

const toFiniteNonNegativeNumber = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string' && !value.trim()) {
    return null;
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }

  return parsed;
};

/* Server-side bookkeeping rows that ride the thread_event stream but carry nothing
   presentable — the analytics brief edges are read only by the hop guard, and without
   this the unknown-type fallback dumps their raw JSON into the feed. */
/* 'analytics.page.reverted' is the undo/redo notice the API appends to a bound thread with no run
   (appendAnalyticsRevertNoticeToBoundThreads.js): the model reads it at the tail of its history,
   the person never sees a card for it. Marker consumed by that service: keep the strings identical. */
/* 'pieve.item.brief' is the cross-item edge (services/pieveItems/constants.js), read only by its guards. */
const HIDDEN_INTERNAL_EVENT_TYPES = new Set(['analytics.page.brief', 'analytics.panel.brief', 'analytics.page.reverted', 'pieve.item.brief']);

const normalizeInteractionType = (value, fallback = '') => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (normalized) {
    return normalized;
  }

  return fallback;
};

const normalizeInteractionOptions = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((option, index) => {
      if (!option || typeof option !== 'object' || Array.isArray(option)) {
        const label = String(option || '').trim();

        if (!label) {
          return null;
        }

        return {
          id: `option-${index + 1}`,
          label,
          value: label
        };
      }

      const optionId = String(option?.id || option?.key || option?.value || `option-${index + 1}`).trim();
      const optionLabel = String(option?.label || option?.name || option?.title || optionId).trim();

      if (!optionLabel) {
        return null;
      }

      return {
        id: optionId || `option-${index + 1}`,
        label: optionLabel,
        value: option?.value !== undefined ? option.value : optionId
      };
    })
    .filter(Boolean);
};

const resolveFirstArtifactArray = (...candidates) => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
};

const isUiActivityShape = (event) => {
  const type = String(event?.type || '');

  if (!KNOWN_ACTIVITY_TYPES.has(type)) {
    return false;
  }

  return Boolean(event?.id || event?.eventId || Number.isFinite(Number(event?.seq)));
};

const hasBooleanApprovalDecisionShape = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  return typeof value?.decision === 'boolean' || typeof value?.remember === 'boolean';
};

const looksLikePermissionApprovalResponse = ({ responseObject, payload = {}, base = {} } = {}) => {
  if (hasBooleanApprovalDecisionShape(responseObject)) {
    return true;
  }

  if (hasBooleanApprovalDecisionShape(payload)) {
    return true;
  }

  if (hasBooleanApprovalDecisionShape(base)) {
    return true;
  }

  return false;
};

const requireFunction = ({ value, name }) => {
  if (typeof value !== 'function') {
    throw new Error(`createNormalizeRemoteEvent requires a ${name} function.`);
  }

  return value;
};

const createNormalizeRemoteEvent = ({ buildMessageBubbleText, clone, createId, createTimestamp, normalizeMessageInputFileAttachments, toLines, toPlainText } = {}) => {
  const safeBuildMessageBubbleText = requireFunction({
    value: buildMessageBubbleText,
    name: 'buildMessageBubbleText'
  });
  const safeClone = requireFunction({
    value: clone,
    name: 'clone'
  });
  const safeCreateId = requireFunction({
    value: createId,
    name: 'createId'
  });
  const safeCreateTimestamp = requireFunction({
    value: createTimestamp,
    name: 'createTimestamp'
  });
  const safeNormalizeMessageInputFileAttachments = requireFunction({
    value: normalizeMessageInputFileAttachments,
    name: 'normalizeMessageInputFileAttachments'
  });
  const safeToLines = requireFunction({
    value: toLines,
    name: 'toLines'
  });
  const safeToPlainText = requireFunction({
    value: toPlainText,
    name: 'toPlainText'
  });

  const extractArtifactRef = ({ event, payload }) => {
    const outputPayload = toObject(payload?.output);
    const resultPayload = toObject(payload?.result);
    const dataPayload = toObject(payload?.data);
    const outputMeta = toObject(outputPayload?.meta) || toObject(outputPayload?.data)?.meta || null;
    const resultMeta = toObject(resultPayload?.meta) || toObject(resultPayload?.data)?.meta || null;
    const dataMeta = toObject(dataPayload?.meta);
    const rootMeta = toObject(payload?.meta);
    const outputMetaArtifact = toObject(outputMeta?.artifact);
    const resultMetaArtifact = toObject(resultMeta?.artifact);
    const dataMetaArtifact = toObject(dataMeta?.artifact);
    const rootMetaArtifact = toObject(rootMeta?.artifact);
    const nestedMetaArtifact = outputMetaArtifact || resultMetaArtifact || dataMetaArtifact || rootMetaArtifact || null;
    const nestedResultDisplay = toObject(outputMeta?.resultDisplay) || toObject(resultMeta?.resultDisplay) || toObject(dataMeta?.resultDisplay) || toObject(rootMeta?.resultDisplay) || toObject(nestedMetaArtifact?.resultDisplay) || null;
    const resultDisplayType = toSafeSortString(nestedResultDisplay?.type);
    const directId = payload?.artifactId || event?.artifactId || payload?.artifact?.artifactId || payload?.artifact?.id || nestedMetaArtifact?.artifactId || nestedMetaArtifact?.id;
    const directUrl = payload?.artifactUrl || event?.artifactUrl || payload?.artifact?.url || nestedMetaArtifact?.artifactUrl || nestedMetaArtifact?.url || nestedMetaArtifact?.downloadUrl || null;
    const listArtifact = Array.isArray(payload?.artifacts) ? payload.artifacts[0] : null;
    const listId = listArtifact?.artifactId || listArtifact?.id || null;
    const listUrl = listArtifact?.url || null;
    const directSurface = payload?.artifact?.surface || nestedMetaArtifact?.surface || listArtifact?.surface || null;

    const artifactId = directId || listId || null;
    const artifactUrl = directUrl || listUrl || null;

    if (!artifactId && !artifactUrl) {
      return null;
    }

    const preview = safeToPlainText(payload?.preview || payload?.bodyPreview || payload?.snippet || payload?.summary || payload?.text || outputPayload?.data?.preview || outputPayload?.preview || nestedMetaArtifact?.preview || payload?.output);

    return {
      artifactId: artifactId ? String(artifactId) : null,
      artifactUrl: artifactUrl ? String(artifactUrl) : null,
      mimeType: payload?.mimeType || payload?.artifact?.mimeType || nestedMetaArtifact?.mimeType || listArtifact?.mimeType || null,
      sizeBytes: payload?.sizeBytes || payload?.artifact?.sizeBytes || nestedMetaArtifact?.sizeBytes || nestedMetaArtifact?.byteLength || listArtifact?.sizeBytes || null,
      preview: truncateText({ value: preview || '', maxLength: 2000 }),
      title: payload?.title || payload?.artifact?.title || listArtifact?.title || nestedMetaArtifact?.fileName || nestedMetaArtifact?.title || 'Artifact',
      surface: directSurface ? String(directSurface) : null,
      resultFormat: toSafeSortString(payload?.artifact?.resultFormat || nestedMetaArtifact?.resultFormat || listArtifact?.resultFormat) || null,
      rendererVersion: toSafeSortString(payload?.artifact?.rendererVersion || nestedMetaArtifact?.rendererVersion || listArtifact?.rendererVersion) || null,
      ...(resultDisplayType
        ? {
            resultDisplay: {
              type: resultDisplayType
            }
          }
        : {})
    };
  };

  const extractArtifactRefs = ({ event, payload }) => {
    const primaryArtifact = extractArtifactRef({
      event,
      payload
    });
    const outputPayload = toObject(payload?.output);
    const resultPayload = toObject(payload?.result);
    const dataPayload = toObject(payload?.data);
    const outputMeta = toObject(outputPayload?.meta) || toObject(outputPayload?.data)?.meta || null;
    const resultMeta = toObject(resultPayload?.meta) || toObject(resultPayload?.data)?.meta || null;
    const dataMeta = toObject(dataPayload?.meta);
    const rootMeta = toObject(payload?.meta);
    const payloadArtifacts = resolveFirstArtifactArray(payload?.artifacts, outputMeta?.artifacts, resultMeta?.artifacts, dataMeta?.artifacts, rootMeta?.artifacts);

    if (payloadArtifacts.length < 1) {
      return primaryArtifact ? [primaryArtifact] : [];
    }

    const outputResultDisplay = toObject(outputMeta?.resultDisplay);
    const resultResultDisplay = toObject(resultMeta?.resultDisplay);
    const dataResultDisplay = toObject(dataMeta?.resultDisplay);
    const rootResultDisplay = toObject(rootMeta?.resultDisplay);
    const seenKeys = new Set();
    const nextArtifacts = [];

    const pushArtifact = (artifact) => {
      if (!artifact) {
        return;
      }

      const artifactId = toSafeSortString(artifact?.artifactId);
      const artifactUrl = toSafeSortString(artifact?.artifactUrl);
      const key = artifactId ? `id:${artifactId}` : artifactUrl ? `url:${artifactUrl}` : '';

      if (key && seenKeys.has(key)) {
        return;
      }

      if (key) {
        seenKeys.add(key);
      }

      nextArtifacts.push(artifact);
    };

    pushArtifact(primaryArtifact);

    for (const rawArtifact of payloadArtifacts) {
      const artifact = toObject(rawArtifact);

      if (!artifact) {
        continue;
      }

      const nestedResultDisplay = toObject(artifact?.resultDisplay) || outputResultDisplay || resultResultDisplay || dataResultDisplay || rootResultDisplay || null;
      const resultDisplayType = toSafeSortString(nestedResultDisplay?.type);
      const preview = safeToPlainText(artifact?.preview || artifact?.text || artifact?.summary || artifact?.snippet || artifact?.content || outputPayload?.data?.preview || outputPayload?.preview);

      pushArtifact({
        artifactId: artifact?.artifactId || artifact?.id ? String(artifact?.artifactId || artifact?.id) : null,
        artifactUrl: artifact?.artifactUrl || artifact?.url || artifact?.downloadUrl ? String(artifact?.artifactUrl || artifact?.url || artifact?.downloadUrl) : null,
        mimeType: artifact?.mimeType || null,
        sizeBytes: artifact?.sizeBytes || artifact?.byteLength || null,
        preview: truncateText({ value: preview || '', maxLength: 2000 }),
        title: artifact?.title || artifact?.fileName || 'Artifact',
        surface: artifact?.surface ? String(artifact.surface) : null,
        resultFormat: toSafeSortString(artifact?.resultFormat) || null,
        rendererVersion: toSafeSortString(artifact?.rendererVersion) || null,
        ...(resultDisplayType
          ? {
              resultDisplay: {
                type: resultDisplayType
              }
            }
          : {})
      });
    }

    return nextArtifacts;
  };

  const normalizeApprovalActivityEvent = ({ eventId, seq, runId, createdAt, payload = {}, base = {} } = {}) => {
    const interactionPayload = asPlainObject(payload?.interaction);
    const payloadApproval = asPlainObject(payload?.approval);
    const interactionApproval = asPlainObject(interactionPayload?.approval);
    const approvalPayload = Object.keys(interactionApproval).length ? interactionApproval : payloadApproval;
    const rememberEnabledFromPayload = approvalPayload?.rememberEnabled ?? payload?.rememberEnabled;
    const alwaysAllowHidden = Boolean(approvalPayload?.alwaysAllowHidden ?? payload?.alwaysAllowHidden);
    const rememberEnabled = typeof rememberEnabledFromPayload === 'boolean' ? rememberEnabledFromPayload : !alwaysAllowHidden;
    const actionsRaw = approvalPayload?.actions ?? payload?.actions ?? interactionPayload?.actions;
    const actions = Array.isArray(actionsRaw) ? actionsRaw : [];

    return {
      id: eventId,
      type: 'approval',
      approvalId: approvalPayload?.approvalId || payload?.approvalId || payload?.interactionId || interactionPayload?.interactionId || eventId,
      targetMode: approvalPayload?.targetMode || payload?.targetMode || null,
      interactionId: payload?.interactionId || interactionPayload?.interactionId || approvalPayload?.approvalId || payload?.approvalId || eventId,
      interactionType: normalizeInteractionType(payload?.interactionType || interactionPayload?.interactionType || interactionPayload?.type || interactionPayload?.kind || approvalPayload?.interactionType || approvalPayload?.type || approvalPayload?.kind, 'permission_approval'),
      title: approvalPayload?.title || payload?.title || interactionPayload?.prompt || interactionPayload?.title || 'Approve action',
      subtitle: approvalPayload?.subtitle || payload?.subtitle || interactionPayload?.subtitle || null,
      detail: safeToPlainText(approvalPayload?.detail || payload?.detail || interactionPayload?.detail || base?.detail || ''),
      rememberEnabled,
      denyLabel: approvalPayload?.denyLabel || payload?.denyLabel || approvalPayload?.alwaysDenyLabel || payload?.alwaysDenyLabel || 'No',
      approveOnceLabel: approvalPayload?.approveOnceLabel || payload?.approveOnceLabel || 'Yes',
      approveRememberLabel: approvalPayload?.approveRememberLabel || payload?.approveRememberLabel || approvalPayload?.alwaysAllowLabel || payload?.alwaysAllowLabel || "Yes and don't ask again",
      responseMode: approvalPayload?.responseMode || payload?.responseMode || interactionPayload?.responseMode || null,
      decision: payload?.decision || approvalPayload?.decision || null,
      actions,
      seq,
      runId,
      createdAt
    };
  };

  const normalizeInteractionActivityEvent = ({ eventId, seq, runId, createdAt, payload = {}, base = {} } = {}) => {
    const interactionPayload = Object.keys(asPlainObject(payload?.interaction)).length ? asPlainObject(payload?.interaction) : asPlainObject(payload);
    const interactionType = normalizeInteractionType(interactionPayload?.interactionType || interactionPayload?.type || interactionPayload?.kind || payload?.interactionType || payload?.type || payload?.kind, 'generic_prompt');
    const interactionApproval = asPlainObject(interactionPayload?.approval);
    const isApprovalInteraction = interactionType === 'permission_approval' || interactionType === 'thread_mode_conversion' || Object.keys(interactionApproval).length > 0;

    if (isApprovalInteraction) {
      return normalizeApprovalActivityEvent({
        eventId,
        seq,
        runId,
        createdAt,
        payload: {
          ...payload,
          ...interactionPayload
        },
        base
      });
    }

    const prompt = safeToPlainText(interactionPayload?.prompt || interactionPayload?.title || interactionPayload?.question || payload?.prompt || payload?.title);
    const detail = safeToPlainText(interactionPayload?.detail || interactionPayload?.description || payload?.detail || base?.detail || '');
    const options = normalizeInteractionOptions(interactionPayload?.options || payload?.options);
    const interactionDataPayload = asPlainObject(interactionPayload?.payload);
    const respondedAt = payload?.respondedAt || null;

    return {
      id: eventId,
      type: 'interaction',
      interactionId: interactionPayload?.interactionId || payload?.interactionId || eventId,
      interactionType,
      title: prompt || 'Input required',
      prompt: prompt || null,
      detail: detail || null,
      options,
      outcome: payload?.outcome || null,
      ...(respondedAt ? { respondedAt } : {}),
      payload: Object.keys(interactionDataPayload).length > 0 ? interactionDataPayload : null,
      seq,
      runId,
      createdAt
    };
  };

  const normalizeInteractionResponseActivityEvent = ({ eventId, seq, runId, createdAt, payload = {}, base = {} } = {}) => {
    const responseSource = payload?.response ?? base?.response ?? payload;
    const responseObject = asPlainObject(responseSource);
    const inferredPermissionApproval = looksLikePermissionApprovalResponse({
      responseObject,
      payload,
      base
    });
    const interactionType = normalizeInteractionType(payload?.interactionType || base?.interactionType, inferredPermissionApproval ? 'permission_approval' : 'generic_prompt');
    const interactionId = payload?.interactionId || base?.interactionId || null;
    const isDeniedPermissionApproval = interactionType === 'permission_approval' && responseObject?.decision === false;

    if (isDeniedPermissionApproval) {
      const guidanceText = safeToPlainText(responseObject?.guidance || responseObject?.message).trim();
      const denialText = truncateText({
        value: guidanceText || 'No',
        maxLength: 600
      });

      return {
        id: interactionId ? `${interactionId}:response` : eventId,
        type: 'bubble',
        className: 'user',
        interactionId,
        interactionType,
        response: responseObject,
        lines: safeToLines(denialText),
        seq,
        runId,
        createdAt
      };
    }

    if (interactionType === 'connection_setup') {
      const interactionPayload = asPlainObject(payload?.interaction);
      const connectionSetupPayload = asPlainObject(payload?.connectionSetup) || asPlainObject(payload?.connection_setup);
      const prompt = safeToPlainText(base?.title || interactionPayload?.title || payload?.title || 'Connect to continue');
      const detail = safeToPlainText(base?.detail || interactionPayload?.detail || payload?.detail || '');
      const outcome = payload?.outcome || (responseObject?.connected === true ? 'connected' : responseObject?.dismissed === true ? 'dismissed' : responseObject?.connected === false ? 'failed' : null);
      const respondedAt = payload?.respondedAt || responseObject?.respondedAt || null;

      return {
        id: interactionId ? `${interactionId}:response` : eventId,
        type: 'interaction',
        interactionId,
        interactionType,
        response: responseObject,
        outcome,
        ...(respondedAt ? { respondedAt } : {}),
        title: prompt || 'Connect to continue',
        prompt: prompt || null,
        detail: detail || null,
        options: [],
        payload: Object.keys(connectionSetupPayload).length > 0 ? connectionSetupPayload : payload,
        seq,
        runId,
        createdAt
      };
    }

    const resolveInteractionResponseSummary = () => {
      const normalizeSummaryKey = (value) => {
        return safeToPlainText(value)
          .trim()
          .replace(/[.!]+$/g, '')
          .replace(/\s+/g, ' ')
          .toLowerCase();
      };
      const isPermissionApprovalTextAlias = (value) => {
        const normalizedSummaryKey = normalizeSummaryKey(value);
        return normalizedSummaryKey === 'yes' || normalizedSummaryKey === "yes and don't ask again";
      };
      const messageCandidates = [responseObject?.message, responseObject?.guidance, responseObject?.statusMessage, payload?.message, payload?.text, payload?.content, base?.message, base?.text, base?.content];

      for (const candidate of messageCandidates) {
        const rawSummary = safeToPlainText(candidate).trim();

        if (!rawSummary) {
          continue;
        }

        if (interactionType === 'permission_approval' && isPermissionApprovalTextAlias(rawSummary)) {
          continue;
        }

        const summary = truncateText({
          value: rawSummary,
          maxLength: 600
        });

        if (summary) {
          return summary;
        }
      }

      if (interactionType === 'permission_approval' && responseObject?.decision === true) {
        if (responseObject?.remember === true) {
          return 'Approved. I will remember this choice.';
        }

        return 'Approved.';
      }

      if (interactionType === 'connection_setup') {
        if (responseObject?.connected === true) {
          return 'Connection ready.';
        }

        if (responseObject?.dismissed === true || toSafeSortString(responseObject?.source).toLowerCase() === 'modal_close') {
          return 'Authentication dismissed.';
        }

        if (responseObject?.connected === false) {
          return 'Connection not ready yet.';
        }
      }

      if (typeof responseSource === 'string' || typeof responseSource === 'number' || typeof responseSource === 'boolean') {
        return truncateText({
          value: safeToPlainText(responseSource).trim(),
          maxLength: 600
        });
      }

      if (responseObject && Object.keys(responseObject).length > 0) {
        return 'Interaction response submitted.';
      }

      return '';
    };
    const summary = resolveInteractionResponseSummary() || 'Interaction response submitted.';

    return {
      id: eventId,
      type: 'line',
      className: 'cmd',
      interactionId,
      interactionType,
      response: responseObject,
      segments: toSegments(summary, 'muted'),
      seq,
      runId,
      createdAt
    };
  };

  const normalizeRemoteEvent = (rawEvent) => {
    const base = rawEvent && typeof rawEvent === 'object' ? rawEvent : { text: String(rawEvent || '') };
    const payload = asPlainObject(base?.payload);
    const eventType = normalizeInteractionType(base?.type || payload?.type || 'line', 'line');
    const seq = Number.isFinite(Number(base?.seq)) ? Number(base.seq) : null;
    const runId = base?.runId || payload?.runId || null;
    const createdAt = base?.createdAt || payload?.createdAt || safeCreateTimestamp();
    const eventId = String(base?.eventId || base?.id || (seq !== null ? `seq-${seq}` : safeCreateId()));
    const shouldForceStructuredNormalization = eventType === 'approval' || eventType === 'interaction' || eventType === 'interaction_response';

    if (HIDDEN_INTERNAL_EVENT_TYPES.has(eventType)) {
      return null;
    }

    if (!shouldForceStructuredNormalization && isUiActivityShape(base)) {
      const normalizedUiEvent = {
        ...safeClone(base),
        id: eventId,
        seq,
        runId: base?.runId || runId,
        createdAt: base?.createdAt || createdAt
      };

      if (toSafeSortString(normalizedUiEvent?.type).toLowerCase() === 'divider' && !toSafeSortString(normalizedUiEvent?.label)) {
        normalizedUiEvent.label = toSafeSortString(payload?.label || base?.payload?.label || 'Update');
      }

      if (toSafeSortString(normalizedUiEvent?.type).toLowerCase() === 'divider') {
        const dividerKind = toSafeSortString(normalizedUiEvent?.dividerKind || payload?.dividerKind || payload?.kind).toLowerCase();
        const anchorEventId = toSafeSortString(normalizedUiEvent?.anchorEventId || payload?.anchorEventId || payload?.anchor_event_id);
        const durationMs = toFiniteNonNegativeNumber(normalizedUiEvent?.durationMs ?? payload?.durationMs ?? payload?.duration_ms);

        if (dividerKind) {
          normalizedUiEvent.dividerKind = dividerKind;
        }

        if (anchorEventId) {
          normalizedUiEvent.anchorEventId = anchorEventId;
        }

        if (durationMs !== null) {
          normalizedUiEvent.durationMs = durationMs;
        }
      }

      return normalizedUiEvent;
    }

    // Persisted model reasoning (the collapsed "Reasoning" disclosure). Display-only: the
    // backend excludes these from model input; here they just carry their text to the feed.
    if (eventType === 'reasoning') {
      const text = safeToPlainText(payload?.text || base?.text);

      if (!text.trim()) {
        return null;
      }

      return {
        id: eventId,
        type: 'reasoning',
        text,
        seq,
        runId,
        createdAt
      };
    }

    if (eventType === 'message') {
      if (shouldHideMessageInActivityFeed({ payload })) {
        return null;
      }

      const role = String(payload?.role || base?.role || '').toLowerCase();
      const attachments = role === 'user' ? safeNormalizeMessageInputFileAttachments({ payload, base }) : [];
      const text = safeBuildMessageBubbleText({
        text: safeToPlainText(payload?.text || payload?.content || base?.text || base?.content),
        attachments
      });

      return {
        id: eventId,
        type: 'bubble',
        className: role === 'user' ? 'user' : undefined,
        ...(role === 'assistant' && typeof payload.assistantTextFinal === 'boolean' ? { assistantTextFinal: payload.assistantTextFinal } : {}),
        ...(role === 'assistant' && payload.segmentId ? { segmentId: payload.segmentId } : {}),
        lines: safeToLines(text),
        ...(attachments.length > 0
          ? {
              attachments
            }
          : {}),
        seq,
        runId,
        createdAt
      };
    }

    if (eventType === 'tool_result' || eventType === 'tool_results') {
      const artifacts = extractArtifactRefs({ event: base, payload });
      // Spilled tool_result stubs persisted before the spill kept callId inline
      // only carry it in the function_call_output context item.
      const contextItemCallId = String(payload?.contextInputItem?.call_id || base?.contextInputItem?.call_id || '').trim();
      const callId = String(payload?.callId || payload?.toolCallId || base?.callId || base?.toolCallId || contextItemCallId || '').trim() || null;
      const rawResultToolName = String(payload?.toolName || payload?.name || base?.toolName || '').trim();
      // Native and historical plan-run results carry Claude Code's tool names.
      const { toolName, hidden: isHiddenHarnessStep } = toPieveEquivalentToolCall({ toolName: rawResultToolName, harness: payload?.harness });
      const storedToolResultOutput = payload?.output && typeof payload.output === 'object' && !Array.isArray(payload.output) ? payload.output : payload?.result && typeof payload.result === 'object' && !Array.isArray(payload.result) ? payload.result : null;
      const rawToolResultOutput = storedToolResultOutput;
      const hiddenArtifactPreview = isSpillStubPayload(payload);

      if (artifacts.length > 0) {
        return artifacts.map((artifact, index) => {
          return {
            id: index === 0 ? eventId : `${eventId}:artifact:${index + 1}`,
            sourceEventId: eventId,
            type: 'artifactPreview',
            title: artifact.title,
            preview: artifact.preview,
            artifactId: artifact.artifactId,
            artifactUrl: artifact.artifactUrl,
            mimeType: artifact.mimeType,
            sizeBytes: artifact.sizeBytes,
            surface: artifact.surface,
            resultFormat: artifact.resultFormat,
            rendererVersion: artifact.rendererVersion,
            producerSucceeded: rawToolResultOutput?.success === true,
            resultDisplay: artifact.resultDisplay || null,
            callId,
            toolName: toolName || null,
            toolResultOutput: rawToolResultOutput ? safeClone(rawToolResultOutput) : null,
            hiddenInActivityFeed: hiddenArtifactPreview,
            seq,
            runId,
            createdAt
          };
        });
      }

      const code = safeToPlainText(payload?.output || payload?.result || payload?.text || base?.text);
      const normalizedToolName = toSafeSortString(toolName).toLowerCase();
      const shouldPreserveChecklistUpdatePayload = normalizedToolName === CHECKLIST_UPDATE_TOOL_NAME;
      const toolResultOutput = parseToolResultOutputFromEvent({
        output: rawToolResultOutput,
        code
      });
      const hiddenInActivityFeed =
        isHiddenHarnessStep ||
        shouldHideToolResultInActivityFeed({
          toolName,
          output: toolResultOutput
        });

      return {
        id: eventId,
        type: 'codeBlock',
        language: payload?.language || 'text',
        code: truncateText({ value: code || '', maxLength: 20_000 }),
        toolName: toolName || null,
        callId,
        hiddenInActivityFeed,
        ...(shouldPreserveChecklistUpdatePayload
          ? {
              result: payload?.result && typeof payload.result === 'object' ? safeClone(payload.result) : payload?.result || null,
              output: payload?.output && typeof payload.output === 'object' ? safeClone(payload.output) : payload?.output || null
            }
          : {}),
        seq,
        runId,
        createdAt
      };
    }

    if (eventType === 'tool_call') {
      const rawToolName = payload?.name || payload?.toolName || base?.toolName || 'tool';
      const callId = String(payload?.callId || payload?.toolCallId || base?.callId || base?.toolCallId || '').trim() || null;
      // A plan run's tool call carries Claude Code's tool name; it renders as the Pieve tool that does the same work.
      const { toolName, args: rawToolCallArgs, hidden: isHiddenHarnessStep } = toPieveEquivalentToolCall({ toolName: rawToolName, args: payload?.args, harness: payload?.harness });
      const toolCallArgs = {
        ...(rawToolCallArgs || {}),
        request: payload?.request,
        response: payload?.response
      };
      const toolCallDisplayText =
        String(payload?.displayText || '').trim() ||
        getPieveToolCallDisplayText({
          toolName,
          args: toolCallArgs
        });
      const toolCallSegments = toToolCallSegments({
        toolName,
        args: toolCallArgs,
        tone: 'muted',
        displayText: toolCallDisplayText
      });
      const hiddenInActivityFeed =
        isHiddenHarnessStep ||
        shouldHideToolCallInActivityFeed({
          toolName,
          args: toolCallArgs
        });

      return {
        id: eventId,
        type: 'line',
        toolName: String(toolName || ''),
        callId,
        toolArgs: safeClone(toolCallArgs),
        displayText: toolCallDisplayText || null,
        displayMeta: payload?.displayMeta && typeof payload.displayMeta === 'object' && !Array.isArray(payload.displayMeta) ? safeClone(payload.displayMeta) : null,
        segments: toolCallSegments,
        hiddenInActivityFeed,
        seq,
        runId,
        createdAt
      };
    }

    if (eventType === 'diff') {
      const files = Array.isArray(payload?.files)
        ? payload.files.map((file) => {
            return {
              path: file?.path || '',
              additions: Number(file?.additions || 0),
              deletions: Number(file?.deletions || 0)
            };
          })
        : [];

      return {
        id: eventId,
        type: 'fileChangeCard',
        title: payload?.title || 'File changes',
        files,
        seq,
        runId,
        createdAt
      };
    }

    if (eventType === 'approval') {
      return normalizeApprovalActivityEvent({
        eventId,
        seq,
        runId,
        createdAt,
        payload,
        base
      });
    }

    if (eventType === 'interaction') {
      return normalizeInteractionActivityEvent({
        eventId,
        seq,
        runId,
        createdAt,
        payload,
        base
      });
    }

    if (eventType === 'interaction_response') {
      return normalizeInteractionResponseActivityEvent({
        eventId,
        seq,
        runId,
        createdAt,
        payload,
        base
      });
    }

    if (eventType === 'sources') {
      const sources = Array.isArray(payload?.sources)
        ? payload.sources.map((source) => {
            return {
              title: source?.title || source?.url || '',
              url: source?.url || ''
            };
          })
        : [];

      return {
        id: eventId,
        type: 'sources',
        title: payload?.title || 'Sources',
        sources,
        seq,
        runId,
        createdAt
      };
    }

    if (eventType === 'divider') {
      const dividerKind = toSafeSortString(payload?.dividerKind || payload?.kind || base?.dividerKind || base?.kind).toLowerCase() || null;
      const anchorEventId = toSafeSortString(payload?.anchorEventId || payload?.anchor_event_id || base?.anchorEventId || base?.anchor_event_id) || null;
      const durationMs = toFiniteNonNegativeNumber(payload?.durationMs ?? payload?.duration_ms ?? base?.durationMs ?? base?.duration_ms);

      return {
        id: eventId,
        type: 'divider',
        label: String(payload?.label || base?.label || 'Update'),
        ...(dividerKind
          ? {
              dividerKind
            }
          : {}),
        ...(anchorEventId
          ? {
              anchorEventId
            }
          : {}),
        ...(durationMs !== null
          ? {
              durationMs
            }
          : {}),
        seq,
        runId,
        createdAt
      };
    }

    if (eventType === 'trigger_payload') {
      const triggerPayload = asPlainObject(payload?.triggerPayload);
      const events = Array.isArray(triggerPayload?.events) ? safeClone(triggerPayload.events) : [];
      const eventCount = toFiniteNonNegativeNumber(triggerPayload?.eventCount) ?? events.length;

      return {
        id: eventId,
        type: 'triggerPayloadCard',
        triggerPayload: Object.keys(triggerPayload).length > 0 ? safeClone(triggerPayload) : null,
        source: toSafeSortString(triggerPayload?.source) || null,
        appId: toSafeSortString(triggerPayload?.appId) || null,
        actionId: toSafeSortString(triggerPayload?.actionId) || null,
        events,
        eventCount,
        rawPayload: safeClone(payload),
        seq,
        runId,
        createdAt
      };
    }

    /* An answer that landed in the thread's inbox (C5). The model reads it at exactly this place, so
       the person sees a mark at the same place; the answer itself lives on the action in the rail. */
    if (eventType === 'action_answered') {
      const source = toSafeSortString(payload?.by) === 'portal' ? 'An action was answered on the Full Service portal' : 'An action was answered in the app';

      return {
        id: eventId,
        type: 'line',
        segments: toSegments(`${source}.`),
        seq,
        runId,
        createdAt
      };
    }

    if (eventType === 'error') {
      const text = safeToPlainText(payload?.message || payload?.text || base?.message || base?.text || 'Run failed');

      return {
        id: eventId,
        type: 'bubble',
        lines: safeToLines(`Error: ${text}`),
        seq,
        runId,
        createdAt
      };
    }

    const fallbackText = safeToPlainText(payload?.text || payload?.message || base?.text || base?.message || payload || base);

    return {
      id: eventId,
      type: 'line',
      segments: toSegments(truncateText({ value: fallbackText, maxLength: 1200 }), 'muted'),
      seq,
      runId,
      createdAt
    };
  };

  return normalizeRemoteEvent;
};

export { asPlainObject, toSafeSortString };
export default createNormalizeRemoteEvent;
